/**
 * Represents a single user session entry in the session history table.
 * Each row maps to one online session recorded by the backend.
 */
export interface UserSessionEntry {
  /** Unique session identifier */
  id: string;
  /** UTC timestamp of the session start */
  date: Date;
  /** User identifier (UUID) */
  userId: string;
  /** Name of the VR experience the session was in */
  experienceName: string;
  /** Session duration in seconds */
  durationSeconds: number;
  /** Country of the user */
  country?: string;
  /** Multiplayer session identifier — absent for solo sessions */
  multiplayerSessionId?: string;
}

/**
 * Represents a single seat assignment entry in the seat history table.
 * Each row maps to one individual seat assigned through a license.
 */
export interface SeatHistoryEntry {
  /** Unique seat assignment identifier */
  id: string;
  /** Name of the license that granted this seat (e.g. "Company A – Fire in the Lab") */
  grantedViaLicense: string;
  /** Anonymized / obfuscated user identifier (UUID) */
  anonymizedUser: string;
  /** UTC date when the seat was assigned */
  dateAssigned: Date;
  /** Current expiry date of the seat (UTC) — may differ from original if extended/shortened */
  currentExpiryDate?: Date;
  /** Original expiry date at the time of assignment (UTC) */
  originalExpiryDate?: Date;
}

/**
 * One row of the User Usage table: all sessions of a single user, aggregated
 * over the selected date range.
 */
export interface UserUsageEntry {
  /** Row identifier — the user id */
  id: string;
  /** User identifier (UUID) */
  userId: string;
  /** Number of sessions recorded in the selected range */
  sessionCount: number;
  /** Number of distinct experiences the user was in */
  uniqueExperienceCount: number;
  /** Sum of all session durations in seconds */
  totalDurationSeconds: number;
}

/**
 * Showing-dropdown options.
 *
 * The string values are persisted in session storage, so changing them
 * invalidates any selection a user already has stored.
 */
export enum ShowingOption {
  SEAT_HISTORY = "seatHistory",
  USER_SESSIONS = "userSessions",
  USER_USAGE = "userUsage",
}
